"use client";

import { SafeHydrate } from "@/SafeHydrate";
import { ArrowRight, Loader, Calendar, Globe, MapPin, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CircleRegistrationForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-10 bg-[#EFE6DC] rounded-lg text-center space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <div className="size-16 bg-[#A77B62] rounded-lg flex items-center justify-center mx-auto shadow-md">
          <span className="text-2xl"></span>
        </div>
        <div className="space-y-4">
          <h3 className="font-serif text-3xl text-[#43362F]">Merci pour ton inscription</h3>
          <div className="font-sans text-[#43362F]/80 space-y-4 leading-relaxed">
            <p>Tu recevras prochainement un e-mail avec les informations de paiement de l'acompte de 50 CHF.</p>
            <p>Ta place sera confirmée dès réception de cet acompte.</p>
            <p>J'ai très hâte de te retrouver tout bientôt avec ton bébé.</p>
            <p className="font-serif text-xl pt-2">Isabel</p>
          </div>
        </div>
      </div>;
  }
  const situationOptions = ["Je suis enceinte", "J’ai déjà accouché", "Je me renseigne pour quelqu’un d’autre afin de lui offrir le cercle en cadeau"];
  const languageOptions = ["Français", "Anglais", "Les deux", "Autre"];
  const sessionOptions = ["Cercle maman & bebe Septembre 2026", "Cercle maman & bebe Janvier 2027"];
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "Le prénom est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Prénom *</FormLabel>
              <FormControl>
                <Input placeholder="Ton prénom" {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="last_name" rules={{
        required: "Le nom est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Nom *</FormLabel>
              <FormControl>
                <Input placeholder="Ton nom" {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "L'adresse e-mail est requise",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Email invalide"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Adresse e-mail *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="email@exemple.com" {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="phone" rules={{
        required: "Le numéro de téléphone est requis"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Numéro de téléphone / WhatsApp *</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+41 79 000 00 00" {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 px-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="location" rules={{
      required: "Ce champ est requis"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Dans quelle ville ou région habites-tu ? *</FormLabel>
            <FormControl>
              <div className="relative">
                <MapPin className="absolute left-0 top-3 size-4 text-[#D5C3B1]" />
                <Input placeholder="Ex: Genève, Nyon..." {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 pl-6 pr-0" />
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="situation" rules={{
      required: "Veuillez choisir une option"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Quelle est ta situation actuellement ? *</FormLabel>
            <FormControl>
              <div className="grid gap-3">
                {situationOptions.map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 p-3 rounded-lg border border-[#D5C3B1]/30 hover:bg-[#EFE6DC]/50 cursor-pointer transition-colors">
                    <input type="radio" name="situation" value={option} checked={field.value === option} onChange={() => field.onChange(option)} className="size-4 accent-[#A77B62]" />
                    <span className="font-sans text-sm text-[#43362F]">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="baby_date" rules={{
      required: "La date est requise"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Date prévue d’accouchement ou date de naissance de ton bébé *</FormLabel>
            <FormControl>
              <div className="relative">
                <SafeHydrate><Calendar className="absolute left-0 top-3 size-4 text-[#D5C3B1]" /></SafeHydrate>
                <Input type="date" {...field} className="h-12 bg-transparent border-0 border-b border-[#D5C3B1] rounded-lg focus-visible:border-[#A77B62] focus-visible:ring-0 pl-6 pr-0" />
              </div>
            </FormControl>
            <FormDescription className="text-[#A77B62] italic text-xs mt-2">
              Cela me permet de vérifier que le cercle correspond bien à l’âge de ton bébé au moment du programme.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="languages" rules={{
      required: "Veuillez choisir une option"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <div className="space-y-2">
              <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Quelle(s) langue(s) comprends-tu ? *</FormLabel>
              <p className="text-xs text-[#43362F]/60 leading-relaxed">
                Le cercle pourra accueillir des mamans francophones et anglophones. Les spécialistes sont bilingues, mais certaines parties pourront se dérouler en français selon la composition du groupe. Une compréhension de base du français est donc recommandée.
              </p>
            </div>
            <FormControl>
              <div className="grid grid-cols-2 gap-3">
                {languageOptions.map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 p-3 rounded-lg border border-[#D5C3B1]/30 hover:bg-[#EFE6DC]/50 cursor-pointer transition-colors">
                    <input type="radio" name="languages" value={option} checked={field.value === option} onChange={() => field.onChange(option)} className="size-4 accent-[#A77B62]" />
                    <span className="font-sans text-sm text-[#43362F]">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="p-6 bg-[#EFE6DC]/50 rounded-lg space-y-4 border border-[#D5C3B1]/20">
        <div className="flex gap-3">
          <Info className="size-5 text-[#A77B62] shrink-0 mt-0.5" />
          <div className="space-y-4 font-sans text-sm text-[#43362F] leading-relaxed">
            <p>
              Après l’envoi de ce formulaire, tu recevras par e-mail les informations nécessaires pour réserver ta place ainsi que les détails de paiement de l’acompte de 50 CHF.
            </p>
            <p>
              Ta place sera officiellement réservée uniquement après réception de l’acompte. Celui-ci sera déduit du montant final. Le solde pourra être réglé en une ou deux fois.
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 border border-[#D5C3B1] rounded-lg space-y-4 bg-white/50">
        <h3 className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Conditions d’inscription</h3>
        <div className="font-sans text-sm text-[#43362F]/80 space-y-3 leading-relaxed">
          <p>Le tarif Early Bird est de <strong>480 CHF</strong> jusqu’au 23.08.2026.</p>
          <p>Le tarif standard est de <strong>500 CHF</strong> dès le 24.08.2026.</p>
          <p>Un acompte de 50 CHF est demandé pour réserver ta place. Il sera déduit du montant final.</p>
          <p>L’acompte n’est pas remboursable en cas de désistement de ta part.</p>
          <p>Il sera remboursé intégralement uniquement :</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>si le cercle ne peut pas avoir lieu par manque de participantes ;</li>
            <li>si la cohorte ou une séance est annulée de notre côté ;</li>
            <li>si les dates exactes communiquées ne te conviennent finalement pas.</li>
          </ul>
          <p className="pt-2 italic">Les rencontres auront principalement lieu les mercredis de 9h30 à 11h00, avec une séance prévue un lundi. Les dates exactes seront communiquées prochainement.</p>
        </div>
      </div>

      <FormField control={control} name="accept_terms" rules={{
      required: "Vous devez accepter les conditions pour continuer"
    }} render={({
      field
    }) => <FormItem className="space-y-0">
            <div className="flex items-start gap-3">
              <FormControl>
                <Checkbox checked={field.value} onCheckedChange={field.onChange} className="mt-1 border-[#D5C3B1] data-[state=checked]:bg-[#A77B62] data-[state=checked]:border-[#A77B62] rounded-md" />
              </FormControl>
              <FormLabel className="font-sans text-sm text-[#43362F] cursor-pointer leading-relaxed">
                J’ai lu et j’accepte les conditions d’inscription ci-dessus. *
              </FormLabel>
            </div>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="circle_session" rules={{
      required: "Veuillez choisir une session"
    }} render={({
      field
    }) => <FormItem className="space-y-4">
            <FormLabel className="font-sans uppercase tracking-widest text-[10px] text-[#A77B62] font-bold">Choisissez votre session *</FormLabel>
            <FormControl>
              <div className="grid gap-3">
                {sessionOptions.map((option, i) => <label key={option} data-index={i} className="flex items-center gap-3 p-3 rounded-lg border border-[#D5C3B1]/30 hover:bg-[#EFE6DC]/50 cursor-pointer transition-colors">
                    <input type="radio" name="circle_session" value={option} checked={field.value === option} onChange={() => field.onChange(option)} className="size-4 accent-[#A77B62]" />
                    <span className="font-sans text-sm text-[#43362F]">{option}</span>
                  </label>)}
              </div>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-[#A77B62] hover:bg-[#43362F] text-[#F8F4ED] rounded-lg font-sans uppercase tracking-widest text-xs font-bold transition-all duration-500 shadow-md group">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-4" />
            Envoi en cours...
          </> : <>
            Envoyer mon inscription
            <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </>}
      </Button>
    </Form>;
}
export default function CircleEnrollmentForm() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="enrollment-form" className="py-24 bg-[#F8F4ED]">
      <div ref={ref} className={`container mx-auto px-4 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-7 space-y-10">
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <span className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-[#A77B62]">
                  Inscription
                </span>
                <div className="h-px w-12 bg-[#A77B62]" />
              </div>
              <h2 className="font-serif text-4xl md:text-5xl text-[#43362F] leading-tight">
                Inscription au Cercle <span className="tracking-wider">Maman & Bébé</span>
              </h2>
              <p className="font-sans font-light text-lg text-[#43362F]/70 max-w-xl leading-relaxed">
                Remplis ce formulaire pour demander ton inscription à la prochaine cohorte du Cercle Maman & Bébé.
              </p>
            </div>

            <FormProvider formId="circle-maman-bebe-registration" sectionName="Circle Enrollment" defaultValues={{
            first_name: "",
            last_name: "",
            email: "",
            phone: "",
            location: "",
            situation: "",
            baby_date: "",
            languages: "",
            accept_terms: false,
            circle_session: ""
          }}>
              <CircleRegistrationForm />
            </FormProvider>
          </div>

          <div className="lg:col-span-5 sticky top-24">
            <div className="relative">
              <div className="aspect-[4/5] overflow-hidden rounded-tl-[120px] rounded-lg shadow-xl">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/postnatal_care_session.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/postnatal_care_session.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/postnatal_care_session.webp-wvc-sizes" alt="Maman et bébé dans un moment de douceur" className="w-full h-full object-cover" loading="lazy" src="https://quality-chicken.10web.cloud/wp-content/uploads/2026/06/b9a44a98-b10d-4781-9918-b6826cd60e94.png" />
              </div>
              
              <div className="absolute -bottom-8 -left-8 size-48 bg-[#EFE6DC] rounded-lg -z-10" />
              
              <div className="absolute -right-4 top-1/2 -translate-y-1/2 flex flex-col gap-2">
                {[1, 2, 3].map(i => <div key={i} data-index={i} className="size-1.5 rounded-lg bg-[#D5C3B1]" />)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}